import * as THREE from 'three';
import {AbstractPointSnapStrategy} from './AbstractPointSnapStrategy';
import {PointSnapStrategyType} from './PointSnapStrategyType';
import type {PointSnapRequest, PointSnapResult} from './PointSnapTypes';



/**
 * 角度捕捉策略。
 * 职责：
 * 1. 读取 PointSnapManager 中设置的角度步长（angleSnapDegree）；
 * 2. 以 referenceDirection（上一条线方向）或参考平面水平轴为基准，
 *    计算当前预览线与基准方向的夹角；
 * 3. 当夹角接近 angleSnapDegree 的整数倍时，将预览终点投影到对应角度方向上；
 * 4. 在 PointSnapResult.snapAngleDeg 中附带命中角度，供 SketchDrawingManager
 *    在预览线末端显示角度标签。
 */
export class AnglePointSnapStrategy extends AbstractPointSnapStrategy {
    /** 策略类型。 */
    public readonly strategyType: PointSnapStrategyType = PointSnapStrategyType.Angle;

    /**
        * 角度捕捉屏幕像素阈值。
     * 说明：
        * 1. 使用“当前鼠标屏幕点到最近目标角度线段的像素距离”判断是否进入吸附；
        * 2. 当像素偏离距离不超过 5px 时触发角度吸附；
        * 3. 改为固定屏幕阈值后，近景与远景下的手感更加稳定，不再随几何长度变化。
     */
        private static readonly ANGLE_SNAP_SCREEN_DISTANCE_THRESHOLD_PX = 5;

    /**
     * 执行角度捕捉。
     * 说明：
     * 1. 无绘制起点时返回 null；
     * 2. 计算当前预览方向与基准方向在参考平面 2D 坐标系中的夹角；
    * 3. 找到最近的 angleSnapDegree 倍数，并按鼠标点到目标角度射线的偏离距离判定是否命中；
     * 4. 返回投影点并附带 snapAngleDeg，供调用方显示角度标签。
     * @param request 点捕捉请求参数（包含 drawingFirstPoint 和 referenceDirection）。
     * @returns 角度捕捉结果；未命中时返回 null。
     */
    public resolve(request: PointSnapRequest): PointSnapResult | null {
        const firstPoint = request.drawingFirstPoint ?? null;
        // 无绘制起点时角度捕捉不生效。
        if (!firstPoint) {
            return null;
        }

        // 获取参考平面（用于在 2D 坐标系中计算角度）。
        const referencePlane = this.viewer?.currentReferencePlane ?? null;
        if (!referencePlane) {
            return null;
        }

        // 读取角度步长设置。
        const angleDegStep = this.viewer?.pointSnapManager?.angleSnapDegree ?? 45;
        if (angleDegStep <= 0) {
            return null;
        }

        const rawPoint = request.worldPosition;

        // 将起点和当前鼠标位置投影到参考平面 2D 坐标系。
        const local1 = referencePlane.worldToLocal(firstPoint);
        const local2 = referencePlane.worldToLocal(rawPoint);

        const dx = local2.x - local1.x;
        const dy = local2.y - local1.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        // 距离过短时不判断方向。
        if (dist < 1e-6) {
            return null;
        }

        // 计算当前预览方向的绝对角度（相对参考平面 X 轴，0° = 向右，逆时针为正）。
        const currentAngleRad = Math.atan2(dy, dx);
        const currentAngleDeg = currentAngleRad * (180 / Math.PI);

        // 计算基准方向角度。
        // 若提供了 referenceDirection，将其投影到参考平面 2D 并计算角度；否则以 0°（X 轴正方向）为基准。
        let baseDeg = 0;
        if (request.referenceDirection) {
            // 将世界坐标参考方向投影到参考平面 2D，得到参考角度。
            const refEnd = firstPoint.clone().add(request.referenceDirection);
            const refLocal = referencePlane.worldToLocal(refEnd);
            baseDeg = Math.atan2(refLocal.y - local1.y, refLocal.x - local1.x) * (180 / Math.PI);
        }

        // 当前预览方向相对基准方向的夹角（[-180, 180]）。
        const relativeAngleDeg = currentAngleDeg - baseDeg;

        // 找到最近的 angleSnapDegree 倍数。
        const snappedMultiple = Math.round(relativeAngleDeg / angleDegStep);
        const snappedRelativeDeg = snappedMultiple * angleDegStep;

        // 先计算吸附后的绝对角度（参考平面 2D 坐标系）。
        const snappedAbsoluteDeg = baseDeg + snappedRelativeDeg;
        const snappedRad = snappedAbsoluteDeg * (Math.PI / 180);

        // 将鼠标距离投影到吸附角度方向，得到吸附点（保持与起点的 2D 距离不变）。
        const snapLocalX = local1.x + dist * Math.cos(snappedRad);
        const snapLocalY = local1.y + dist * Math.sin(snappedRad);
        const snapWorld = referencePlane.localToWorld(new THREE.Vector2(snapLocalX, snapLocalY));

        
        // 使用“当前鼠标屏幕点到目标角度线段的像素距离”做命中判断，避免吸附窗口随几何长度变化。
        const screenStart = this.projectWorldToScreen(firstPoint);
        const screenSnap = this.projectWorldToScreen(snapWorld);
        if (!screenStart || !screenSnap) {
            return null;
        }

        const screenDistance = AbstractPointSnapStrategy.pointToSegmentDistance(
            request.screenPosition,
            screenStart,
            screenSnap,
        );
        if (screenDistance > AnglePointSnapStrategy.ANGLE_SNAP_SCREEN_DISTANCE_THRESHOLD_PX) {
            return null;
        }
        

        // 规范化显示角度（取相对基准的正角度，0~360）。
        const displayAngleDeg = ((snappedRelativeDeg % 360) + 360) % 360;

        return {
            strategyType: this.strategyType,
            position: snapWorld,
            indicatorPosition: snapWorld,
            targetObject: null,
            // 角度捕捉权重低于切线/垂线/平行/水平竖直，高于普通点捕捉。
            weight: 600,
            snapAngleDeg: displayAngleDeg,
        };
    }
}


